// ── TransactionEditor.js — 거래 입력/수정 모달 ──────────────────────────────
// PRD V1 Sprint 0: 거래 1건 입력 5분 이내, 카테고리/증빙/사업개인 마킹까지 끝.

import React, { useState, useEffect, useMemo } from 'react';
import {
  CATEGORY_MAJOR, CATEGORY_SUB, REVENUE_CATEGORIES,
  EVIDENCE_TYPES, BIZ_TYPES, TRANSACTION_TYPES,
  VAT_RATE, splitVAT,
  isQualifiedEvidence, isVATDeductible, isPresumedDeductionCandidate,
  evaluatePresumedDeduction,
  newTransaction, newPartner,
  categoryLabel,
} from '../data/taxConstants';

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('ko-KR');

export default function TransactionEditor({
  initial,                // 수정 시 기존 거래, 신규 시 null
  defaultType = 'expense',
  partners,               // [{id, name, bizNumber, ...}]
  onSave,                 // async (txn) => void
  onSavePartner,          // async (partner) => string (partnerId)
  onClose,
  onDelete,               // 수정 시만
}) {
  const [txn, setTxn] = useState(() => initial ? { ...initial } : { ...newTransaction(defaultType) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [partnerSuggest, setPartnerSuggest] = useState([]);

  const isExpense = txn.type === 'expense';
  const isIncome  = txn.type === 'income';

  // 거래처 자동완성
  useEffect(() => {
    if (!txn.partnerName) { setPartnerSuggest([]); return; }
    const q = txn.partnerName.trim().toLowerCase();
    if (!q) { setPartnerSuggest([]); return; }
    const list = (partners || [])
      .filter(p => (p.name || '').toLowerCase().includes(q))
      .slice(0, 5);
    setPartnerSuggest(list);
  }, [txn.partnerName, partners]);

  // 부가세 자동 계산: total 입력 시 supply/vat 분리
  const setTotal = (totalStr) => {
    const total = Number(String(totalStr).replace(/[^\d.-]/g, '')) || 0;
    const { supply, vat } = splitVAT(total);
    setTxn(t => ({ ...t, total, supply, vat }));
  };

  const setEvidence = (evidenceType) => {
    const qualified = isQualifiedEvidence(evidenceType);
    setTxn(t => ({
      ...t,
      evidence: { ...t.evidence, type: evidenceType, isQualified: qualified },
    }));
  };

  const setMajor = (majorId) => {
    setTxn(t => ({ ...t, category: { major: majorId, sub: null } }));
  };

  const setSub = (subId) => {
    setTxn(t => ({ ...t, category: { ...t.category, sub: subId } }));
  };

  const setType = (typeId) => {
    setTxn(t => ({
      ...t,
      type: typeId,
      category: { major: typeId === 'income' ? null : 'material', sub: null },
      revenueCategory: typeId === 'income' ? 'sales_card' : null,
      vatTreatment: { ...t.vatTreatment, deductible: typeId === 'expense' },
    }));
  };

  const setPersonalToggle = (isPersonal) => {
    setTxn(t => ({
      ...t,
      incomeTreatment: {
        ...t.incomeTreatment,
        isPersonal,
        isBusinessExpense: !isPersonal,
      },
    }));
  };

  const handlePartnerPick = (partner) => {
    setTxn(t => ({
      ...t,
      partnerId: partner.id,
      partnerName: partner.name,
    }));
    setPartnerSuggest([]);
  };

  const validate = () => {
    if (!txn.date) return '거래일자를 입력해주세요';
    if (!txn.total || txn.total <= 0) return '금액을 입력해주세요';
    if (isExpense && !txn.category?.major) return '카테고리를 선택해주세요';
    if (isExpense && !txn.evidence?.type) return '증빙 종류를 선택해주세요';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true); setError(null);
    try {
      // 거래처 처리: 이름은 있는데 partnerId가 없으면 신규 거래처 생성
      let partnerId = txn.partnerId;
      if (!partnerId && txn.partnerName?.trim()) {
        const existing = (partners || []).find(p =>
          (p.name || '').trim().toLowerCase() === txn.partnerName.trim().toLowerCase()
        );
        if (existing) {
          partnerId = existing.id;
        } else if (onSavePartner) {
          const np = newPartner();
          np.name = txn.partnerName.trim();
          partnerId = await onSavePartner(np);
        }
      }
      // 의제매입 자동 마킹 — 품목명 + 거래처명 + bizType + 증빙 종합 판정
      const partnerObj = (partners || []).find(p => p.id === partnerId);
      const evalResult = isExpense ? evaluatePresumedDeduction({
        itemName: txn.itemName,
        partnerName: txn.partnerName,
        bizType: partnerObj?.bizType,
        evidenceType: txn.evidence?.type,
      }) : { candidate: false, confidence: 0, reason: null };

      // confidence ≥ 0.85 → 자동 마킹 (사용자 토글 안 했어도 ON)
      // 0.6 ≤ confidence < 0.85 → 사용자 확인 필요 (needsReview), 사용자 토글 우선
      // < 0.6 → 자동 마킹 X, 사용자 토글만 반영
      const userToggle = !!txn.vatTreatment?.presumed;
      const autoMark = evalResult.candidate && evalResult.confidence >= 0.85;
      const needsReview = evalResult.candidate && evalResult.confidence < 0.85 && !userToggle;
      const presumed = userToggle || autoMark;

      const finalTxn = {
        ...txn,
        partnerId,
        vatTreatment: {
          ...txn.vatTreatment,
          deductible: isVATDeductible({ ...txn, partnerId, vatTreatment: { ...txn.vatTreatment, presumed } }),
          presumed,
          presumedReason: presumed ? evalResult.reason : null,
          presumedConfidence: presumed ? evalResult.confidence : null,
        },
        needsReview,
        confidence: evalResult.confidence,
      };
      await onSave(finalTxn);
      onClose && onClose();
    } catch (e) {
      console.error(e);
      setError(e.message || '저장 실패');
      setSaving(false);
    }
  };

  // ── 스타일
  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16,
  };
  const modal = {
    background: '#fff', borderRadius: 14, padding: 0,
    width: 560, maxWidth: '100%', maxHeight: '92vh', overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
  };
  const header = {
    padding: '14px 18px', borderBottom: '1px solid #eee',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  };
  const body = { padding: 18, overflow: 'auto', flex: 1 };
  const footer = {
    padding: '12px 18px', borderTop: '1px solid #eee',
    display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center',
  };
  const sectionTitle = { fontSize: 11, fontWeight: 700, color: '#888', marginTop: 14, marginBottom: 6, letterSpacing: 0.5 };
  const inputStyle = {
    width: '100%', padding: '8px 10px', border: '1px solid #ddd',
    borderRadius: 7, fontSize: 14, outline: 'none', boxSizing: 'border-box',
  };
  const chipBase = {
    padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
    cursor: 'pointer', userSelect: 'none', border: '1px solid',
  };

  const Chip = ({ active, color, onClick, children }) => (
    <button onClick={onClick} type="button"
      style={{
        ...chipBase,
        borderColor: active ? (color || '#FF6F0F') : '#e5e7eb',
        background: active ? (color || '#FF6F0F') : '#fff',
        color: active ? '#fff' : '#555',
      }}>
      {children}
    </button>
  );

  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}>
      <div style={modal}>
        {/* 헤더 */}
        <div style={header}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>
            {initial && initial.id ? '거래 수정' : (initial?.source === 'ocr' ? '📸 OCR 결과 확인' : '거래 추가')}
          </div>
          <button onClick={onClose} type="button"
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>×</button>
        </div>

        {/* 본문 */}
        <div style={body}>
          {/* 매출/매입 토글 */}
          <div style={sectionTitle}>거래 종류</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {TRANSACTION_TYPES.map(t => (
              <Chip key={t.id}
                active={txn.type === t.id}
                color={t.id === 'income' ? '#FF6F0F' : '#71717A'}
                onClick={() => setType(t.id)}>
                {t.id === 'income' ? '↑' : '↓'} {t.label}
              </Chip>
            ))}
          </div>

          {/* 일자 + 금액 */}
          <div style={sectionTitle}>거래일자 / 금액</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <input type="date" value={txn.date}
              onChange={e => setTxn({ ...txn, date: e.target.value })}
              style={{ ...inputStyle, flex: '0 0 160px' }} />
            <div style={{ flex: 1, position: 'relative' }}>
              <input type="text" inputMode="numeric" placeholder="합계 금액 (부가세 포함)"
                value={txn.total ? fmt(txn.total) : ''}
                onChange={e => setTotal(e.target.value)}
                style={{ ...inputStyle, textAlign: 'right' }} />
              <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#888' }}>원</span>
            </div>
          </div>
          {txn.total > 0 && (
            <div style={{ fontSize: 11, color: '#888', marginTop: 6, textAlign: 'right' }}>
              공급가액 {fmt(txn.supply)}원 + 부가세 {fmt(txn.vat)}원 = {fmt(txn.total)}원
            </div>
          )}

          {/* 거래처 */}
          <div style={sectionTitle}>거래처</div>
          <div style={{ position: 'relative' }}>
            <input type="text" placeholder="거래처 상호 (예: 농협하나로마트)"
              value={txn.partnerName}
              onChange={e => setTxn({ ...txn, partnerName: e.target.value, partnerId: null })}
              style={inputStyle} />
            {partnerSuggest.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 2,
                background: '#fff', border: '1px solid #ddd', borderRadius: 7,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 5, maxHeight: 200, overflow: 'auto',
              }}>
                {partnerSuggest.map(p => (
                  <div key={p.id} onClick={() => handlePartnerPick(p)}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f5f5f5' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                    <div style={{ fontWeight: 600 }}>{p.name}</div>
                    {p.bizNumber && <div style={{ fontSize: 11, color: '#888' }}>{p.bizNumber}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 품목 (의제매입 자동 후보 표시) */}
          {isExpense && (
            <>
              <div style={sectionTitle}>품목 <span style={{ fontWeight: 400, color: '#aaa' }}>(선택)</span></div>
              <input type="text" placeholder="예: 한우 등심, 양배추, A4 용지"
                value={txn.itemName || ''}
                onChange={e => setTxn({ ...txn, itemName: e.target.value })}
                style={inputStyle} />
              {(() => {
                const partnerObj = (partners || []).find(p => p.id === txn.partnerId);
                const ev = evaluatePresumedDeduction({
                  itemName: txn.itemName,
                  partnerName: txn.partnerName,
                  bizType: partnerObj?.bizType,
                  evidenceType: txn.evidence?.type,
                });
                if (!ev.candidate) return null;
                if (ev.confidence >= 0.85) {
                  return (
                    <div style={{ fontSize: 11, color: '#18181B', marginTop: 6, fontWeight: 600 }}>
                      ✨ 의제매입세액공제 자동 적용 ({ev.reason}, 신뢰도 {Math.round(ev.confidence * 100)}%)
                    </div>
                  );
                }
                return (
                  <div style={{ fontSize: 11, color: '#EA580C', marginTop: 6, fontWeight: 600 }}>
                    ⚠ 의제매입공제 대상일 수 있습니다 ({ev.reason}). 고급 옵션에서 확인 필요.
                  </div>
                );
              })()}
            </>
          )}

          {/* 카테고리 (매입) / 매출 카테고리 */}
          {isExpense && (
            <>
              <div style={sectionTitle}>카테고리</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {CATEGORY_MAJOR.map(m => (
                  <Chip key={m.id}
                    active={txn.category?.major === m.id}
                    color={m.color}
                    onClick={() => setMajor(m.id)}>
                    {m.emoji} {m.label}
                  </Chip>
                ))}
              </div>
              {txn.category?.major && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                  {(CATEGORY_SUB[txn.category.major] || []).map(s => (
                    <button key={s.id} type="button" onClick={() => setSub(s.id)}
                      style={{
                        padding: '4px 9px', borderRadius: 6, fontSize: 11,
                        border: '1px solid', borderColor: txn.category?.sub === s.id ? '#FF6F0F' : '#e5e7eb',
                        background: txn.category?.sub === s.id ? '#FAFAFA' : '#fff',
                        color: txn.category?.sub === s.id ? '#FF6F0F' : '#666',
                        cursor: 'pointer',
                      }}>
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {isIncome && (
            <>
              <div style={sectionTitle}>매출 종류</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {REVENUE_CATEGORIES.map(r => (
                  <Chip key={r.id}
                    active={txn.revenueCategory === r.id}
                    color="#FF6F0F"
                    onClick={() => setTxn({ ...txn, revenueCategory: r.id })}>
                    {r.label}
                  </Chip>
                ))}
              </div>
            </>
          )}

          {/* 증빙 */}
          <div style={sectionTitle}>증빙</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {EVIDENCE_TYPES.map(e => (
              <Chip key={e.id}
                active={txn.evidence?.type === e.id}
                color={e.qualified ? '#FF6F0F' : '#9ca3af'}
                onClick={() => setEvidence(e.id)}>
                {e.qualified ? '✓' : '⚠'} {e.label}
              </Chip>
            ))}
          </div>
          {txn.evidence?.type && !txn.evidence.isQualified && (
            <div style={{ fontSize: 11, color: '#EA580C', marginTop: 6 }}>
              ⚠ 비적격증빙 — 가산세 2% 위험. 가능하면 세금계산서/카드/현금영수증으로.
            </div>
          )}

          {/* 사업/개인 토글 */}
          <div style={sectionTitle}>사용 구분</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Chip
              active={!txn.incomeTreatment?.isPersonal}
              color="#FF6F0F"
              onClick={() => setPersonalToggle(false)}>
              💼 사업용
            </Chip>
            <Chip
              active={txn.incomeTreatment?.isPersonal}
              color="#9ca3af"
              onClick={() => setPersonalToggle(true)}>
              🏠 개인용
            </Chip>
          </div>

          {/* 메모 */}
          <div style={sectionTitle}>메모 <span style={{ fontWeight: 400, color: '#aaa' }}>(선택)</span></div>
          <input type="text" placeholder="비고, 영수증 보관 위치 등"
            value={txn.memo || ''}
            onChange={e => setTxn({ ...txn, memo: e.target.value })}
            style={inputStyle} />

          {/* 고급 옵션 토글 */}
          <button type="button" onClick={() => setShowAdvanced(!showAdvanced)}
            style={{
              marginTop: 14, background: 'none', border: 'none', color: '#18181B',
              fontSize: 12, cursor: 'pointer', padding: 0, fontWeight: 600,
            }}>
            {showAdvanced ? '▲ 고급 옵션 닫기' : '▼ 고급 옵션 (의제매입공제·연결 등)'}
          </button>

          {showAdvanced && isExpense && (
            <div style={{ marginTop: 10, padding: 12, background: '#f9fafb', borderRadius: 7 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 6 }}>
                <input type="checkbox"
                  checked={!!txn.vatTreatment?.presumed}
                  onChange={e => setTxn({ ...txn, vatTreatment: { ...txn.vatTreatment, presumed: e.target.checked } })} />
                의제매입세액공제 대상 (면세 농축수산물)
              </label>
              <div style={{ fontSize: 11, color: '#888' }}>
                (V1.5에서 품목명·거래처 기반 자동 마킹 + 환급 추정액 카드)
              </div>
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div style={footer}>
          {error && <div style={{ flex: 1, color: '#71717A', fontSize: 12 }}>{error}</div>}
          {initial && initial.id && onDelete && (
            <button onClick={() => { if(window.confirm('이 거래를 삭제하시겠습니까?')) { onDelete(initial.id); onClose && onClose(); } }}
              type="button"
              style={{ marginRight: 'auto', padding: '8px 14px', border: 'none', background: 'none', color: '#71717A', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              🗑 삭제
            </button>
          )}
          <button onClick={onClose} type="button"
            style={{ padding: '8px 16px', border: '1px solid #ddd', background: '#fff', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
            취소
          </button>
          <button onClick={handleSave} type="button" disabled={saving}
            style={{
              padding: '8px 18px', border: 'none', background: '#FF6F0F',
              color: '#fff', borderRadius: 7, cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 700, opacity: saving ? 0.6 : 1,
            }}>
            {saving ? '저장 중...' : (initial && initial.id ? '수정 완료' : '거래 추가')}
          </button>
        </div>
      </div>
    </div>
  );
}
